import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { places } from '../data/places';
export function CultureHistoryPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const culturalPlaces = places.filter(p => p.categories.includes('Culture') || p.categories.includes('Historical') || p.categories.includes('Art & Museums'));
  const culturalAspects = [{
    title: 'Colonial Heritage',
    description: "From the railway depot of 1899 to independence in 1963, Nairobi's colonial past is preserved in museums, architecture, and historical sites.",
    highlights: ['Karen Blixen Museum', 'Railway Museum', 'Parliament Buildings']
  }, {
    title: 'Diverse Communities',
    description: "Home to all 42 Kenyan ethnic groups plus Asian, European, and other African communities, Nairobi is Kenya's melting pot.",
    highlights: ['Bomas of Kenya', 'Cultural Centers', 'Traditional Performances']
  }, {
    title: 'Contemporary Arts',
    description: 'A thriving arts scene with galleries, studios, and public art showcasing East African creativity and innovation.',
    highlights: ['Nairobi Gallery', 'GoDown Arts Centre', 'Street Art']
  }, {
    title: 'Music & Performance',
    description: "From traditional tribal music to modern Afrobeat and gospel, Nairobi's music scene is diverse and vibrant.",
    highlights: ['Live Music Venues', 'Cultural Festivals', 'Theater Productions']
  }];
  return <div className="min-h-screen bg-white" data-id="element-637">
      {/* Hero */}
      <div className="border-b border-gray-200" data-id="element-638">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-32" data-id="element-639">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-640">
            <h1 className="text-5xl md:text-7xl font-semibold text-charcoal mb-6 leading-tight" data-id="element-641">
              Culture & History
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed" data-id="element-642">
              From railway depot to regional powerhouse, discover the stories
              that shaped East Africa's most dynamic city.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-id="element-643">
        {/* Introduction */}
        <section className="mb-20" data-id="element-644">
          <div className="max-w-3xl" data-id="element-645">
            <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-646">
              Nairobi's cultural tapestry is woven from threads of indigenous
              Maasai heritage, colonial British influence, Asian
              entrepreneurship, and post-independence African renaissance.
              Founded in 1899 as a railway depot, the city has evolved into a
              cosmopolitan hub where traditional tribal customs coexist with
              cutting-edge contemporary art.
            </p>
            <p className="text-gray-700 leading-relaxed" data-id="element-647">
              The city's museums, galleries, and cultural centers tell stories
              of struggle and triumph, of colonial oppression and independence,
              of cultural preservation and creative evolution.
            </p>
          </div>
        </section>

        {/* Cultural Aspects */}
        <section className="mb-20" data-id="element-648">
          <h2 className="text-2xl font-semibold text-charcoal mb-8" data-id="element-649">
            Cultural Dimensions
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-650">
            {culturalAspects.map((aspect, index) => <motion.div key={aspect.title} className="border border-gray-200 p-8" initial={{
            y: 20,
            opacity: 0
          }} whileInView={{
            y: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-651">
                <h3 className="text-xl font-semibold text-charcoal mb-3" data-id="element-652">
                  {aspect.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-653">
                  {aspect.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-654">
                  {aspect.highlights.map(highlight => <span key={highlight} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs" data-id="element-655">
                      {highlight}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* Places */}
        <section data-id="element-656">
          <h2 className="text-2xl font-semibold text-charcoal mb-8" data-id="element-657">
            Cultural & Historical Sites
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-658">
            {culturalPlaces.map((place, index) => <motion.div key={place.id} initial={{
            y: 20,
            opacity: 0
          }} whileInView={{
            y: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-659">
                <Link to={`/place/${place.id}`} className="group block" data-id="element-660">
                  <div className="border border-gray-200 hover:border-charcoal transition-smooth overflow-hidden" data-id="element-661">
                    <div className="relative aspect-[4/3] overflow-hidden" data-id="element-662">
                      <img src={place.image} alt={place.name} className="w-full h-full object-cover group-hover:scale-105 transition-smooth" data-id="element-663" />
                    </div>
                    <div className="p-6" data-id="element-664">
                      <h3 className="text-xl font-semibold text-charcoal mb-2 group-hover:underline" data-id="element-665">
                        {place.name}
                      </h3>
                      <p className="text-gray-700 text-sm line-clamp-2 mb-4" data-id="element-666">
                        {place.description}
                      </p>
                      <div className="flex items-center gap-2 text-charcoal font-medium text-sm" data-id="element-667">
                        <span data-id="element-668">Discover</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} data-id="element-669" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>)}
          </div>
        </section>
      </div>

      <ScrollToTop data-id="element-670" />
      <Footer data-id="element-671" />
    </div>;
}